import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getDestination, getDestinationList } from '@/services/destination.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { QueryKey } from '@/lib/queryKeys';

export const useDestinationsList = (
  params: {
    pagination: { limit: number | null; offset: number | null };
    sort: { order: string; value: string };
    queryString: string;
  },
  options: object,
) => {
  const destinationListQueryFn = useAuthenticatedQueryFn(getDestinationList);

  const {
    data: destinationList = {
      destinations: [],
      pagination: { count: 0, limit: 0, offset: 0 },
      sort: { order: SortOrderOptions.ASC, value: SortOrderValues.CREATED_AT },
    },
    isPending,
    isFetching,
    isFetched,
    error,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.DestinationList, params.pagination, params.queryString, params.sort],
    queryFn: () => destinationListQueryFn(params),
    placeholderData: keepPreviousData,
    ...options,
  });

  return {
    data: destinationList.destinations,
    pagination: destinationList.pagination,
    isFetching,
    isPending,
    isFetched,
    error,
    refetch,
  };
};

export const useDestination = (id: string, options?: object) => {
  const destinationQueryFn = useAuthenticatedQueryFn(getDestination);

  const {
    data: destination,
    isPending: destinationIsPending,
    isFetching: destinationIsFetching,
    error: destinationError,
    refetch: refetchDestination,
  } = useQuery({
    queryKey: [QueryKey.Destination, id],
    queryFn: () => destinationQueryFn(id),
    ...options,
  });

  return {
    destination,
    destinationIsFetching,
    destinationIsPending,
    destinationError,
    refetchDestination,
  };
};
